import FormContainer from "@client/content/controls/Form/container";
import { getRootContainerProps } from "@client/content/lib/helper";
import { type ModuleApiInfo } from "@client/content/selectors";
import { type RootContainerProps } from "@client/content/types";
import { type FormFieldInfo, type FormHandles } from "@client/controls/Form/component";
import Select, { DynamicSelect } from "@client/controls/Inputs/Select/component";
import withIsVisibleHOC, { type WithIsVisibleHOCProps } from "@client/hocs/withIsVisible";
import { type OptionItem } from "@client/types";
import { useI18n } from "@common/hooks/i18n";
import getOptions from "@common/lib/options";
import { identity } from "lodash";
import * as React from "react";
import { withOnSubmit } from "../../hooks";
import { type Checkout } from "../../types";

export type EventSettings = {
  displayEvents: boolean;
  eventsMode: "all" | "specific" | "unlinked";
  events: string[] | undefined;
  eventRequired: boolean;
};

export type MapProps = RootContainerProps &
  WithIsVisibleHOCProps & {
    apiInfo: ModuleApiInfo;
    eventDateLocationId: string | null;
    eventId: string | null;
    eventOptions: Checkout.EventOptionItem[];
    settings: EventSettings;
    shouldShowEvents: boolean;
  };

export interface ActionProps {
  onChange: (name: keyof Checkout.View.Event, value: string) => void;
}

export type EventOption = OptionItem<string> & {
  portalId: string | null;
};
export type Props = ActionProps & MapProps;

export default withIsVisibleHOC((props: Props) => {
  const i18n = useI18n();

  const options = React.useMemo(() => getOptions(i18n), [i18n]);

  const ref = withOnSubmit<FormHandles>(identity);

  const [eventId, setEventId] = React.useState(props.eventId);

  React.useEffect(() => setEventId(props.eventId), [props.eventId, setEventId]);

  const matchingEventOptions = React.useMemo((): OptionItem<string>[] => {
    if (props.settings.eventsMode === "unlinked") {
      return props.eventOptions.filter((x) => !x.portalId);
    }

    if (props.settings.eventsMode === "specific") {
      if (!props.settings.events) return [];

      return props.eventOptions.filter((x) => props.settings.events!.includes(x.value));
    }
    return props.eventOptions;
  }, [props.eventId, props.eventOptions, props.settings.eventsMode, props.settings.events]);

  const fields = React.useMemo<FormFieldInfo[]>(() => {
    const fields: FormFieldInfo[] = [];

    if (props.shouldShowEvents) {
      fields.push({
        Component: Select,
        label: i18n.event,
        name: "eventId",
        isRequired: props.settings.eventRequired,
        defaultValue: eventId,
        componentOpts: {
          options: matchingEventOptions,
          sort: "asc"
        },
        qa: "Event"
      });
    }

    if (eventId) {
      fields.push({
        Component: DynamicSelect,
        label: i18n.stand,
        name: "eventDateLocationId",
        isRequired: props.settings.eventRequired || !!props.eventId,
        defaultValue: props.eventDateLocationId,
        componentOpts: {
          options: options.getGetOptions<string>("expo", "eventDateLocations", {
            eventId: eventId
          }),
          sort: "asc",
          sortType: "natural"
        },
        qa: "Stand"
      });
    }

    return fields;
  }, [
    eventId,
    matchingEventOptions,
    props.eventDateLocationId,
    props.settings.eventRequired,
    props.shouldShowEvents,
    props.apiInfo
  ]);

  const onChange = React.useCallback(
    (name: keyof Checkout.View.Event, value: string) => {
      if (name === "eventId") {
        setEventId(value);
      }

      props.onChange(name, value);
    },
    [props.onChange, setEventId]
  );

  return (
    <div className="event">
      <h4>{i18n.event}</h4>

      <FormContainer
        {...getRootContainerProps(props)}
        ref={ref}
        fields={fields}
        onChange={onChange}
        validateOn="change"
      />
    </div>
  );
});
